import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** --------- 1) HOW TO APPLY --------- **/
export function HowToApply({ steps }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0,0,0,.06), 0 12px 28px rgba(0,0,0,.08)",
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <CardContent>
        <Typography
          sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 18, sm: 20 } }}
        >
          How to Apply
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mb: 2,
            fontSize: { xs: 14.5, sm: 15, md: 16 },
          }}
        >
          Follow these steps to complete your application.
        </Typography>

        <Stepper orientation="vertical" activeStep={-1}>
          {steps.map((s, idx) => (
            <Step key={idx} sx={{ fontSize: { xs: 11, sm: 12 } }}>
              <StepLabel>
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15 } }}
                >
                  {s.title}
                </Typography>
              </StepLabel>
              <Box
                sx={{
                  ml: 4,
                  mt: 0.5,
                  mb: 1.5,
                  color: "text.secondary",
                  fontSize: { xs: 13.5, sm: 14.5 },
                }}
              >
                {s.desc}
              </Box>
            </Step>
          ))}
        </Stepper>
      </CardContent>
    </Card>
  );
}

/** --------- 2) ELIGIBILITY CRITERIA --------- **/
export function EligibilityCriteria({ bullets = [], tags = [] }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0,0,0,.06), 0 12px 28px rgba(0,0,0,.08)",
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <CardContent>
        <Typography
          sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 18, sm: 20 } }}
        >
          Eligibility Criteria
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 3,
            lineHeight: 1.9,
            fontSize: { xs: 13.5, sm: 14.5 },
          }}
        >
          {bullets.map((b, i) => (
            <li key={i}>
              <Typography
                component="span"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: 11.5, sm: 12.5 },
                }}
              >
                {b}
              </Typography>
            </li>
          ))}
        </Box>

        {tags?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {tags.map((t, i) => (
                <Chip key={i} label={t} size="small" />
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** --------- 3) IMPORTANT DATES --------- **/
export function ImportantDates({ rows = [] }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0,0,0,.06), 0 12px 28px rgba(0,0,0,.08)",
        border: (t) => `1px solid ${t.palette.divider}`,
        overflowX: "auto",
      }}
    >
      <CardContent>
        <Typography
          sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 18, sm: 20 } }}
        >
          Important Dates
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mb: 1,
            fontSize: { xs: 13.5, sm: 14.5 },
          }}
        >
          Mark these deadlines to avoid last-minute rush.
        </Typography>

        <Table size="small" aria-label="important dates">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    fontSize: { xs: 12.5, sm: 13.5 },
                  }}
                >
                  {r.event}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    fontSize: { xs: 12.5, sm: 13.5 },
                  }}
                >
                  {r.start}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    fontSize: { xs: 12.5, sm: 13.5 },
                  }}
                >
                  {r.end}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.status}
                    color={
                      r.status === "Open"
                        ? "success"
                        : r.status === "Upcoming"
                        ? "primary"
                        : "default"
                    }
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** --------- 4) COUNSELING & ADMISSION (EXPECTATIONS) --------- **/
export function CounselingGuide({ items = [] }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0,0,0,.06), 0 12px 28px rgba(0,0,0,.08)",
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <CardContent>
        <Typography
          sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 18, sm: 20 } }}
        >
          Counseling & Admission — What to Expect
        </Typography>

        {items.map((it, i) => (
          <Accordion
            key={i}
            disableGutters
            sx={{ boxShadow: "none", borderRadius: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                sx={{ fontWeight: 700, fontSize: { xs: 12, sm: 13 } }}
              >
                {it.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: 14.5, sm: 15.5 },
                  lineHeight: 1.8,
                }}
              >
                {it.desc}
              </Typography>
              {it.list?.length ? (
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 3,
                    mt: 1.25,
                    lineHeight: 1.9,
                    fontSize: { xs: 13.5, sm: 14.5 },
                  }}
                >
                  {it.list.map((li, k) => (
                    <li key={k}>{li}</li>
                  ))}
                </Box>
              ) : null}
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
}

/** --------- WRAPPER SECTION (optional) --------- **/
export default function AdmissionEssentials() {
  // Example data (replace with real data or fetch)
  const steps = [
    {
      title: "Fill Application",
      desc: "Complete the form with accurate details and upload required documents.",
    },
    {
      title: "Pay Application Fee",
      desc: "Use UPI / Netbanking / Card. Save the receipt for reference.",
    },
    // {
    //   title: "Submit & Download Acknowledgement",
    //   desc: "Keep the PDF copy for counseling day.",
    // },
    {
      title: "Attend Counseling",
      desc: "Bring originals for verification and choose your stream.",
    },
  ];

  const eligibility = [
    "Passed Class 10th (for Class 11 admission) or Class 12th (for higher admission) from a recognized board.",
    "Minimum aggregate per stream requirement (as notified) applies.",
    "Transfer Certificate (TC), Conduct Certificate, and recent passport-size photographs.",
    "Original mark sheets and migration certificate (if applicable).",
  ];
  const tags = ["JAC / CBSE / ICSE", "NIOS", "IGCSE", "Other State Boards"];

  const importantDates = [
    {
      event: "Application Window",
      start: "Jun 01",
      end: "Sep 30",
      status: "Open",
    },
    {
      event: "Counseling Round 1",
      start: "Jul 05",
      end: "Jul 10",
      status: "Upcoming",
    },
    {
      event: "Fee Payment Deadline",
      start: "Within 3 days of allotment",
      end: "—",
      status: "—",
    },
  ];

  const counselingItems = [
    {
      title: "Document Verification",
      desc: "Carry originals along with one set of self-attested photocopies.",
      list: [
        "10th / 12th Mark Sheet",
        "TC / Migration",
        "Aadhaar",
        "Photos",
        "Caste/Category (if applicable)",
      ],
    },
    {
      title: "Stream Allotment",
      desc: "Allotment based on seat availability, merit, and preferences.",
    },
    {
      title: "Fee Payment & Confirmation",
      desc: "Pay admission fee within the deadline to confirm the seat.",
    },
    {
      title: "Orientation & Reporting",
      desc: "Report to the campus on the notified date for orientation and timetable.",
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "background.default" }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <HowToApply steps={steps} />
          </Grid>
          <Grid item xs={12} md={6}>
            <EligibilityCriteria bullets={eligibility} tags={tags} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImportantDates rows={importantDates} />
          </Grid>
          <Grid item xs={12} md={6}>
            <CounselingGuide items={counselingItems} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
